import { logger } from '@curology/logger/client';
import { type FileRejection } from '@curology/ui-components-web';

import { type Brand } from '@/consts/brands';
import { PhotoUploadService } from '@/services/photoUploadService/client';
import { trackEvent, AnalyticsEvent } from '@/utils/analytics';
import { heicConverter } from '@/utils/images/heic';
import { HEIC_MIME_TYPES } from '@/utils/images/types';

const HEIC_CONVERSION_ERROR_MESSAGE =
  'We were unable to convert your photo. Please try again with a JPEG or PNG image.';

import { PHOTO_ID_METADATA } from '../consts/consts';
import { type UploadedPhoto } from '../types/types';

export const getLatestPhotoIdUploadedPhoto = (
  uploadedPhotos: UploadedPhoto[],
  treatmentCategory: string,
) => {
  let latestPhotoIdUploadedPhoto;

  uploadedPhotos.forEach((uploadedPhoto: UploadedPhoto) => {
    const { photo_perspective, treatment_category } = uploadedPhoto.metadata;

    if (
      photo_perspective === PHOTO_ID_METADATA.type &&
      treatment_category === treatmentCategory
    ) {
      latestPhotoIdUploadedPhoto = uploadedPhoto;
    }
  });

  return latestPhotoIdUploadedPhoto;
};

export const getPageTitleAndDescription = (isMinor?: boolean) => {
  if (isMinor) {
    return {
      pageDescription:
        'Like any doctor’s office, we need to see your parent or guardian’s government-issued photo ID. The name on the ID must match the parent or guardian name provided.',
      pageTitle:
        'So close! Upload a photo of your parent or guardian’s license or ID',
    };
  }

  return {
    pageDescription:
      'Like any doctor’s office, we need to see your government-issued photo ID. Your personal information is private and always will be.',
    pageTitle: 'So close! Upload a photo of your license or ID',
  };
};

export const deleteUploadedPhoto = async (
  brand: Brand,
  setSubmitButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>,
  setUploadedPhoto: React.Dispatch<
    React.SetStateAction<UploadedPhoto | undefined>
  >,
  uploadedPhoto?: UploadedPhoto,
) => {
  if (uploadedPhoto) {
    const uploadedPhotoId = uploadedPhoto.id;

    setUploadedPhoto(undefined);
    setSubmitButtonDisabled(true);

    await PhotoUploadService.deletePhoto(brand, uploadedPhotoId);

    trackEvent(AnalyticsEvent.PHOTO_REMOVED, {
      type: PHOTO_ID_METADATA.type,
    });
  }
};

const getFileToBeUploaded = async (files: File[]) => {
  const file = files[0];

  if (HEIC_MIME_TYPES.includes(file.type)) {
    return await heicConverter(file);
  }

  return file;
};

const getFileUploadData = (file: File, treatmentCategory: string) => {
  const data = new FormData();

  data.append('file', file);

  if (PHOTO_ID_METADATA.isForProvider) {
    data.append('isForProvider', 'true');
  }

  if (PHOTO_ID_METADATA.isIdVerification) {
    data.append('isIdVerification', 'true');
  }

  data.append(
    'metadata',
    JSON.stringify({
      photo_perspective: PHOTO_ID_METADATA.type,
      treatment_category: treatmentCategory,
    }),
  );

  return data;
};

export const uploadPhoto = async (
  brand: Brand,
  files: File[],
  setSubmitButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>,
  setUploadedPhoto: React.Dispatch<
    React.SetStateAction<UploadedPhoto | undefined>
  >,
  setUploading: React.Dispatch<React.SetStateAction<boolean>>,
  treatmentCategory: string,
  onError: (error: Error) => void,
  visitId?: number,
) => {
  setUploading(true);

  let file: File;
  try {
    file = await getFileToBeUploaded(files);
  } catch (error) {
    logger.error(error, {
      message: 'Photo ID HEIC conversion failed',
      fileName: files[0]?.name,
    });
    onError(new Error(HEIC_CONVERSION_ERROR_MESSAGE));
    setUploading(false);
    return;
  }

  const data = getFileUploadData(file, treatmentCategory);

  if (visitId) {
    data.append('visit_id', `${visitId}`);
  }

  const { photo } = await PhotoUploadService.uploadPhotos(brand, data);

  if (photo) {
    setUploadedPhoto({
      id: photo.id,
      localUrl: photo.localUrl,
      metadata: {
        photo_perspective: PHOTO_ID_METADATA.type,
        treatment_category: treatmentCategory,
      },
    });

    setSubmitButtonDisabled(false);
  }

  setUploading(false);

  trackEvent(AnalyticsEvent.PHOTO_UPLOADED, {
    type: PHOTO_ID_METADATA.type,
  });
};

export const fileRejected = (
  fileRejections: FileRejection[],
  setError: React.Dispatch<React.SetStateAction<Error | undefined>>,
) => {
  logger.log('Signup Photo Rejected', fileRejections);

  setError(new Error(fileRejections?.[0].errors[0].message));
};
